import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { get, post } from 'data/services/apiService';
import { API_URL } from 'data/services/apiConstants';
import { showToast } from 'data/reducers/toastReducer';
import { subscribeToConversation, setPusherAuthToken } from 'data/services/pusherService';

const buildMentionHandle = (user = {}) => {
  if (!user) {
    return '';
  }

  const nameHandle = String(user.name || '')
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, '');

  if (nameHandle) {
    return nameHandle;
  }

  const emailHandle = String(user.email || '')
    .split('@')[0]
    ?.toLowerCase()
    .replace(/[^a-z0-9_.-]/g, '');

  if (emailHandle) {
    return emailHandle;
  }

  if (user.id !== undefined && user.id !== null) {
    return `user${user.id}`;
  }

  return '';
};

const formatDateTime = (value) => {
  if (!value) {
    return '';
  }

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString();
  } catch (error) {
    return '';
  }
};

const usePortfolioMessages = (portfolioId, portfolioUsers = []) => {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.user.authToken);
  const currentUser = useSelector((state) => state.user.userData);

  // Set Pusher auth token when it changes
  useEffect(() => {
    if (authToken) {
      setPusherAuthToken(authToken);
    }
  }, [authToken]);

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  // Messages state
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);

  // Message input state
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Create conversation state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [createConversationError, setCreateConversationError] = useState(null);

  // Mention state
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(-1);

  // Refs for UI interactions
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const mentionSuggestionsRef = useRef(null);

  // Computed values
  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => String(conversation.id) === String(selectedConversationId)
      ) || null,
    [conversations, selectedConversationId]
  );

  // Users available for mentions in messages (conversation participants only)
  const mentionableUsers = useMemo(() => {
    const map = new Map();

    // Only use participants from the selected conversation for mentions
    if (selectedConversation && selectedConversation.participants) {
      (selectedConversation.participants || []).forEach((user) => {
        if (user?.id !== undefined && user?.id !== null) {
          const handle = buildMentionHandle(user);
          map.set(user.id, {
            ...user,
            handle: handle || `user${user.id}`,
          });
        }
      });
    }

    return Array.from(map.values());
  }, [selectedConversation]);

  // Users available for participant selection when creating a conversation
  // (portfolio users excluding the logged-in user)
  const availableParticipants = useMemo(() => {
    const map = new Map();
    const currentUserId = currentUser?.id ? String(currentUser.id) : null;

    // Use portfolio users, excluding the logged-in user
    (portfolioUsers || []).forEach((user) => {
      if (user?.id !== undefined && user?.id !== null) {
        const userId = String(user.id);
        // Skip the current logged-in user - they're automatically added to conversations
        if (currentUserId && userId === currentUserId) {
          return; // Skip current user
        }

        const handle = buildMentionHandle(user);
        map.set(user.id, {
          ...user,
          handle: handle || `user${user.id}`,
        });
      }
    });

    return Array.from(map.values());
  }, [portfolioUsers, currentUser]);

  const mentionSuggestions = useMemo(() => {
    if (!showMentionSuggestions) {
      return [];
    }

    if (!mentionQuery) {
      return mentionableUsers.slice(0, 8);
    }

    const query = mentionQuery.toLowerCase();

    return mentionableUsers
      .filter((user) => user.handle.startsWith(query))
      .slice(0, 8);
  }, [mentionableUsers, mentionQuery, showMentionSuggestions]);

  // API functions
  const fetchConversations = useCallback(() => {
    if (!portfolioId || !authToken) {
      return Promise.resolve();
    }

    setIsLoadingConversations(true);
    setConversationError(null);

    return get({
      dispatch,
      url: `${API_URL}/api/portfolio/${portfolioId}/conversations`,
      authToken,
      onSuccess: (result) => {
        const items = Array.isArray(result) ? result : [];
        setConversations(items);

        if (items.length === 0) {
          setSelectedConversationId(null);
          return;
        }

        setSelectedConversationId((prev) => {
          if (
            prev &&
            items.some(
              (conversation) => String(conversation.id) === String(prev)
            )
          ) {
            return prev;
          }

          return String(items[0].id);
        });
      },
      onError: (error) => {
        setConversationError(error);
        setConversations([]);
        setSelectedConversationId(null);
      },
    }).finally(() => {
      setIsLoadingConversations(false);
    });
  }, [authToken, dispatch, portfolioId]);

  const fetchMessages = useCallback(
    (conversationId) => {
      if (!conversationId || !authToken) {
        setMessages([]);
        return Promise.resolve();
      }

      setIsLoadingMessages(true);
      setMessagesError(null);

      return get({
        dispatch,
        url: `${API_URL}/api/conversations/${conversationId}/messages`,
        authToken,
        onSuccess: (result) => {
          const items = Array.isArray(result) ? result : [];
          setMessages(items);
        },
        onError: (error) => {
          setMessages([]);
          setMessagesError(error);
        },
      }).finally(() => {
        setIsLoadingMessages(false);
      });
    },
    [authToken, dispatch]
  );

  // Socket subscription effect
  useEffect(() => {
    if (!selectedConversationId) {
      return undefined;
    }

    const unsubscribe = subscribeToConversation({
      conversationId: selectedConversationId,
      onMessage: (payload) => {
        if (!payload) {
          return;
        }

        setMessages((prev) => {
          const exists = prev.some((item) => item.id === payload.id);
          if (exists) {
            return prev.map((item) => (item.id === payload.id ? payload : item));
          }

          return [...prev, payload];
        });

        setConversations((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) {
            return prev;
          }

          const updated = prev.map((conversation) =>
            String(conversation.id) === String(payload.conversation_id)
              ? {
                ...conversation,
                last_message_preview: payload.message,
                last_message_at: payload.created_at,
              }
              : conversation
          );

          return updated;
        });
      },
    });

    return () => {
      unsubscribe();
    };
  }, [selectedConversationId]);


  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Mention suggestions scroll effect
  useEffect(() => {
    if (
      selectedMentionIndex >= 0 &&
      mentionSuggestionsRef.current &&
      mentionSuggestions.length > 0
    ) {
      const selectedElement = mentionSuggestionsRef.current.children[
        selectedMentionIndex
      ];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedMentionIndex, mentionSuggestions.length]);

  // Event handlers
  const handleConversationSelect = useCallback((conversationId) => {
    setSelectedConversationId(String(conversationId));
  }, []);

  const handleToggleParticipant = useCallback((userId) => {
    setSelectedParticipantIds((prev) => {
      const numericId = Number(userId);
      const exists = prev.includes(numericId);

      if (exists) {
        return prev.filter((id) => id !== numericId);
      }

      return [...prev, numericId];
    });
  }, []);

  const handleCreateConversation = useCallback(
    (event) => {
      event.preventDefault();

      if (selectedParticipantIds.length === 0) {
        setCreateConversationError('Select at least one participant.');
        return;
      }

      setIsCreatingConversation(true);
      setCreateConversationError(null);

      post({
        dispatch,
        url: `${API_URL}/api/portfolio/${portfolioId}/conversations`,
        authToken,
        data: {
          title: newConversationTitle || null,
          user_ids: selectedParticipantIds,
        },
        onSuccess: (result) => {
          const conversation = result || null;
          if (conversation) {
            setConversations((prev) => [conversation, ...prev]);
            setSelectedConversationId(String(conversation.id));
          }

          setShowCreateForm(false);
          setNewConversationTitle('');
          setSelectedParticipantIds([]);
        },
        onError: (error) => {
          const message = error?.message || 'Unable to create conversation.';
          setCreateConversationError(message);
          dispatch(
            showToast({
              title: 'Conversation Error',
              body: message,
              type: 'error',
            })
          );
        },
      }).finally(() => {
        setIsCreatingConversation(false);
      });
    },
    [
      authToken,
      dispatch,
      newConversationTitle,
      portfolioId,
      selectedParticipantIds,
    ]
  );

  const handleMessageInputChange = useCallback((event) => {
    const value = event.target.value;
    const caretPosition = event.target.selectionStart || value.length;
    const textBeforeCaret = value.slice(0, caretPosition);
    const mentionMatch = textBeforeCaret.match(/@([A-Za-z0-9_.-]*)$/);

    setMessageContent(value);

    if (mentionMatch) {
      setMentionQuery(mentionMatch[1].toLowerCase());
      setShowMentionSuggestions(true);
      setSelectedMentionIndex(-1);
    } else {
      setMentionQuery('');
      setShowMentionSuggestions(false);
      setSelectedMentionIndex(-1);
    }
  }, []);

  const handleMentionSelect = useCallback(
    (user) => {
      if (!messageInputRef.current || !user) {
        return;
      }

      const element = messageInputRef.current;
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      if (!range) {
        return;
      }

      const textNode = range.startContainer;
      const textBeforeCaret = textNode.textContent?.slice(0, range.startOffset) || '';
      const mentionMatch = textBeforeCaret.match(/@([A-Za-z0-9_.-]*)$/);

      if (!mentionMatch) {
        return;
      }

      // Calculate the position in the full text
      const fullText = element.textContent || '';
      const textBeforeMention = fullText.slice(0, fullText.length - textBeforeCaret.length + (range.startOffset - mentionMatch[0].length));
      const textAfterCaret = fullText.slice(fullText.length - textBeforeCaret.length + range.startOffset);

      const handle = user.handle;
      const insertion = `@${handle} `;
      const updatedValue = textBeforeMention + insertion + textAfterCaret;

      setMessageContent(updatedValue);
      setMentionQuery('');
      setShowMentionSuggestions(false);
      setSelectedMentionIndex(-1);
    },
    []
  );

  const handleMessageInputKeyDown = useCallback(
    (event) => {
      if (!showMentionSuggestions || mentionSuggestions.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedMentionIndex((prev) =>
          prev < mentionSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (event.key === 'Enter' && selectedMentionIndex >= 0) {
        event.preventDefault();
        const selectedUser = mentionSuggestions[selectedMentionIndex];
        if (selectedUser) {
          handleMentionSelect(selectedUser);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setShowMentionSuggestions(false);
        setSelectedMentionIndex(-1);
      }
    },
    [showMentionSuggestions, mentionSuggestions, selectedMentionIndex, handleMentionSelect]
  );

  const handleSendMessage = useCallback(
    (event) => {
      event.preventDefault();

      if (!messageContent.trim() || !selectedConversationId) {
        return;
      }

      setIsSending(true);

      post({
        dispatch,
        url: `${API_URL}/api/conversations/${selectedConversationId}/messages`,
        authToken,
        data: {
          message: messageContent,
        },
        onSuccess: (result) => {
          // Don't add message here - let Pusher subscription handle it
          // This prevents duplicate messages
          const newMessage = result || null;
          if (newMessage) {
            // Only update conversation preview, not messages list
            setConversations((prev) =>
              prev.map((conversation) =>
                String(conversation.id) === String(selectedConversationId)
                  ? {
                    ...conversation,
                    last_message_preview: newMessage.message,
                    last_message_at: newMessage.created_at,
                  }
                  : conversation
              )
            );
          }

          setMessageContent('');
          setMentionQuery('');
          setShowMentionSuggestions(false);
          setSelectedMentionIndex(-1);
        },
        onError: (error) => {
          dispatch(
            showToast({
              title: 'Message Error',
              body: error?.message || 'Unable to send message.',
              type: 'error',
            })
          );
        },
      }).finally(() => {
        setIsSending(false);
      });
    },
    [
      authToken,
      dispatch,
      messageContent,
      selectedConversationId,
    ]
  );

  // Format text with mentions for display in contenteditable
  const formatTextWithMentions = useCallback((text) => {
    if (!text) {
      return '';
    }

    const mentionRegex = /@([A-Za-z0-9_.-]+)/g;
    return text.replace(mentionRegex, (match) => {
      return `<span class="portfolio-message-input-mention">${match}</span>`;
    });
  }, []);

  const renderMessageText = useCallback((text, messageMentions = []) => {
    if (!text) {
      return null;
    }

    const segments = [];

    const mentionRegex = /@([A-Za-z0-9_.-]+)/g;
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          value: text.slice(lastIndex, match.index),
        });
      }

      // Always highlight @username patterns as mentions
      segments.push({
        type: 'mention',
        value: match[0],
      });

      lastIndex = mentionRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      segments.push({
        type: 'text',
        value: text.slice(lastIndex),
      });
    }

    return segments.map((segment, index) => {
      if (segment.type === 'mention') {
        return (
          <span key={`mention-${index}`} className="portfolio-message-mention">
            {segment.value}
          </span>
        );
      }

      return <React.Fragment key={`text-${index}`}>{segment.value}</React.Fragment>;
    });
  }, []);

  // Initial data fetching
  useEffect(() => {
    // Clear conversations when portfolioId changes
    setConversations([]);
    setSelectedConversationId(null);
    setMessages([]);

    if (portfolioId && authToken) {
      fetchConversations();
    }
  }, [portfolioId, authToken, fetchConversations]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
    } else {
      setMessages([]);
    }
  }, [fetchMessages, selectedConversationId]);

  return {
    // State
    conversations,
    isLoadingConversations,
    conversationError,
    selectedConversationId,
    selectedConversation,
    messages,
    isLoadingMessages,
    messagesError,
    messageContent,
    isSending,
    showCreateForm,
    newConversationTitle,
    selectedParticipantIds,
    isCreatingConversation,
    createConversationError,
    mentionQuery,
    showMentionSuggestions,
    selectedMentionIndex,
    mentionableUsers,
    availableParticipants,
    mentionSuggestions,
    currentUser,

    // Refs
    messagesEndRef,
    messageInputRef,
    mentionSuggestionsRef,

    // Functions
    setMessageContent,
    setShowCreateForm,
    setNewConversationTitle,
    setSelectedParticipantIds,
    setCreateConversationError,
    setMentionQuery,
    setShowMentionSuggestions,
    setSelectedMentionIndex,
    handleConversationSelect,
    handleToggleParticipant,
    handleCreateConversation,
    handleMessageInputChange,
    handleMessageInputKeyDown,
    handleMentionSelect,
    handleSendMessage,
    renderMessageText,
    formatTextWithMentions,
    fetchConversations,
    fetchMessages,
    formatDateTime,

    // Utility functions
    buildMentionHandle,
  };
};

export default usePortfolioMessages;
